import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { HrailRail, InsertHrailRailBody, UpdateHrailRailBody } from "./types/rails";

export const useHrailRailsApi = () => {
  const api = useApi();

  const getAdminHrailRail = useCallback(
    (railId: string, signal: AbortSignal) => {
      const url = `/admin/settings/hrail/rails/${railId}`;
      return useSWRMutation<HrailRail, CustomError, string>(url, (url) =>
        api.get<HrailRail>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminHrailRails = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/rails`, query);
      return useSWRImmutable<Paginated<HrailRail>, CustomError, string>(url, (url) =>
        api.get<Paginated<HrailRail>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminHrailRails = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/rails`, query);
      return api.get<Paginated<HrailRail>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminHrailRail = useCallback(
    async (body: InsertHrailRailBody): Promise<{ id: string }> => {
      const id = await api.post<{ id: string }, InsertHrailRailBody>(`/admin/settings/hrail/rails`, body);
      mutateCache([`/admin/settings/hrail/rails`]);
      return id;
    },
    [api],
  );

  const updateAdminHrailRail = useCallback(
    async (railId: string, body: UpdateHrailRailBody): Promise<void> => {
      const url = `/admin/settings/hrail/rails/${railId}`;
      await api.put<UpdateHrailRailBody>(url, body);
      mutateCache([`/admin/settings/hrail/rails`], url);
    },
    [api],
  );

  const updateAdminHrailRailVisibility = useCallback(
    async (railId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/rails/${railId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([`/admin/settings/hrail/rails`]);
    },
    [api],
  );

  const updateAdminHrailRailOrder = useCallback(
    async (railId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/rails/${railId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([`/admin/settings/hrail/rails`]);
    },
    [api],
  );

  const deleteAdminHrailRail = useCallback(
    async (railId: string): Promise<void> => {
      await api.del(`/admin/settings/hrail/rails/${railId}`);
      mutateCache([`/admin/settings/hrail/rails`]);
    },
    [api],
  );

  return {
    getAdminHrailRail,
    useAdminHrailRails,
    getAdminHrailRails,
    insertAdminHrailRail,
    updateAdminHrailRail,
    updateAdminHrailRailVisibility,
    updateAdminHrailRailOrder,
    deleteAdminHrailRail,
  };
};
